import React, { useMemo, useState, useEffect } from "react";
import { Carousel } from "react-responsive-carousel";
import { useExpanded, useTable, usePagination, useSortBy } from "react-table";
import {
  Table,
  Modal,
  Button,
  Form,
  Card,
  Icon,
  Dropdown,
  List,
  Divider,
  Popup,
  Header,
} from "semantic-ui-react";
import LoadResolver from "components/LoadResolver";
import { downloadFromUrl } from "services/common";
import { useSelector } from "react-redux";
import ConfirmationModal from "components/ConfirmationModal/ConfirmationModal";
import { toast } from "components/Toast/Toast";
import { useTranslation } from "react-i18next";
import { getZippedAssets } from "services/apiMiddleware";
import AssetImage from "components/AssetImage/AssetImage";
import MediaPreview from "components/MediaPreview/MediaPreview";

const formatInformationLength = (infoText) => {
  const shortInfoText = infoText && infoText.length > 19 ? `${infoText.slice(0, 18)}...` : infoText;
  return shortInfoText;
};

export const ApprovalsTableAdmin = ({ data, dispatch, t }) => {
  const [showApproveCampaignModal, setShowApproveCampaignModal] = useState(false);
  const [showRejectCampaignModal, setShowRejectCampaignModal] = useState(false);
  const [campaign, setCampaign] = useState();

  const handleApproveWholeCampaign = (campaign) => {
    if (campaign && campaign.campaignID) {
      const { campaignID } = campaign;
      try {
        dispatch.approvals.approveWholeCampaign(campaignID);
        toast(undefined, t("approvals_toast_campaign_manually_approved"));
      } catch (error) {
        toast("error", `${t("approvals_error_approving_whole_campaign")}: ${error}`);
      }
    }
  };

  const handleRejectWholeCampaign = (campaign) => {
    if (campaign && campaign.campaignID) {
      const { campaignID } = campaign;
      try {
        dispatch.approvals.rejectWholeCampaign(campaignID);
        toast(undefined, t("approvals_toast_campaign_manually_rejected"));
      } catch (error) {
        toast("error", `${t("approvals_error_rejecting_whole_campaign")}: ${error}`);
      }
    }
  };

  const chunk = (array, size) => {
    const chunkedArr = [];
    for (let i = 0; i < array.length; i += size) {
      chunkedArr.push(array.slice(i, i + size));
    }
    return chunkedArr;
  };

  const columns = useMemo(
    () => [
      {
        Header: t("approvals_columns_header_campaign_name"),
        accessor: "name",
      },
      {
        Header: t("campaign_status"),
        accessor: "status",
        Cell: ({ value }) => (value ? value.replace(/_/g, " ") : "---"),
      },
      {
        Header: t("approvals_columns_header_orderlineid"),
        accessor: "orderlineID",
      },
      {
        Header: t("approvals_columns_header_assets"),
        accessor: "amountOfAssets",
      },
      {
        Header: t("approvals_columns_header_start_time"),
        accessor: "starts",
      },
      {
        Header: t("approvals_columns_header_end_time"),
        accessor: "ends",
      },
      {
        Header: t("approvals_columns_header_deadline"),
        accessor: "deadline",
      },
      {
        Header: "",
        id: "icons-options",
        Cell: function IconRow({ row }) {
          return (
            <div style={{ display: "flex", justifyContent: "end" }}>
              <Popup
                content={t("approvals_reject_campaign")}
                trigger={
                  <Button
                    basic
                    icon
                    size="mini"
                    style={{ pointerEvents: "auto", marginRight: "0.25rem" }}
                    onClick={() => {
                      setCampaign(row.original);
                      setShowRejectCampaignModal(true);
                    }}
                  >
                    <Icon name="close" style={{ pointerEvents: "none" }} />
                  </Button>
                }
                position="top right"
              />
              <Popup
                content={t("approvals_approve_campaign")}
                trigger={
                  <Button
                    basic
                    icon
                    size="mini"
                    style={{ pointerEvents: "auto" }}
                    onClick={() => {
                      setCampaign(row.original);
                      setShowApproveCampaignModal(true);
                    }}
                  >
                    <Icon name="check" style={{ pointerEvents: "none" }} />
                  </Button>
                }
                position="top right"
              />
            </div>
          );
        },
      },
      {
        Header: "",
        id: "icons",
        Cell: function IconRow({ row }) {
          const rotate = row.isExpanded ? 90 : 0;
          return (
            <div style={{ display: "flex", justifyContent: "end" }}>
              <Icon
                className="caret right"
                style={{ transform: `rotate(${rotate}deg)`, transition: `transform .1s ease` }}
              />
            </div>
          );
        },
      },
    ],
    []
  );
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    canPreviousPage,
    canNextPage,
    previousPage,
    nextPage,
    pageOptions,
    setPageSize,
    prepareRow,
    toggleAllRowsExpanded,
    state: { pageIndex, pageSize, expanded },
  } = useTable(
    {
      initialState: { pageIndex: 0, pageSize: 10 },
      columns,
      data: data,
    },
    useSortBy,
    useExpanded,
    usePagination
  );

  const handleDownloadAllAssets = async (assets, campaignId) => {
    try {
      const mapped = assets.map((a) => a.media);
      const downloadUrl = await getZippedAssets(mapped, campaignId);
      downloadFromUrl(downloadUrl);
    } catch (error) {
      toast("error", t("campaign_display_assets_download_error"));
    }
  };

  // Card Grid when <=8 images, carousel when more
  const renderAssets = (assets, campaignId) => {
    return (
      <div>
        <Button
          style={{ marginBottom: "1rem" }}
          primary
          onClick={() => handleDownloadAllAssets(assets, campaignId)}
        >
          Download all assets
        </Button>
        {assets?.length > 8 ? (
          <Carousel showThumbs={false} showStatus={false} showIndicators={false}>
            {chunk(assets, 8).map((chunk, index) => (
              <div key={index} style={{ margin: "3rem" }}>
                <Card.Group itemsPerRow={4}>
                  {chunk.map((asset) => (
                    <CardCustomAdmin key={asset.id} asset={asset} dispatch={dispatch} />
                  ))}
                </Card.Group>
              </div>
            ))}
          </Carousel>
        ) : (
          <Card.Group itemsPerRow={4}>
            {assets?.map((asset) => (
              <CardCustomAdmin key={asset.id} asset={asset} dispatch={dispatch} />
            ))}
          </Card.Group>
        )}
      </div>
    );
  };

  const LoadApprovalAssetsAsync = React.memo(({ campaignId }) => {
    const [, setCampaign] = useState();
    useMemo(() => {
      dispatch.assets.loadCampaignApprovalAssets(campaignId);
      setCampaign(campaignId);
    }, [campaignId]);
    const assets = useSelector((state) => state.assets) || {};
    if (assets) {
      return (
        <>
          <div style={{ marginTop: assets.isLoading && "1rem" }}></div>
          <LoadResolver isLoading={assets.isLoading} hideWhenLoading>
            {renderAssets(assets.approvalItems, campaignId)}
          </LoadResolver>
          <div style={{ marginBottom: assets.isLoading && "4rem" }}></div>
        </>
      );
    }
  });

  const getApprovalTableFlairClassName = (campaignStatus) => {
    switch (campaignStatus) {
      case "CAP_PARTIALLY_REJECTED":
        return "partially_rejected";
      case "CAP_APPROVED":
        return "approved";
      case "CAP_PENDING":
        return "pending";
      case "CAP_REJECTED":
        return "rejected";
      default:
        return "not_cap_status";
    }
  };

  return (
    <div>
      <ConfirmationModal
        data={{
          icon: "check",
          headerText: t("approvals_manually_approve_campaign"),
          text: t("approvals_error_approving_whole_campaign_hint"),
        }}
        open={showApproveCampaignModal}
        setOpen={setShowApproveCampaignModal}
        onSuccess={handleApproveWholeCampaign}
        dataForOnSuccess={campaign}
      />
      <ConfirmationModal
        data={{
          icon: "close",
          headerText: t("approvals_manually_reject_campaign"),
          text: t("approvals_error_rejecting_whole_campaign_hint"),
        }}
        open={showRejectCampaignModal}
        setOpen={setShowRejectCampaignModal}
        onSuccess={handleRejectWholeCampaign}
        dataForOnSuccess={campaign}
      />

      <Table className="approval-table" {...getTableProps()}>
        <Table.Header>
          {headerGroups.map((headerGroup, idx) => (
            <Table.Row
              className="approval-table-header-row"
              key={idx}
              {...headerGroup.getHeaderGroupProps()}
            >
              {headerGroup.headers.map((column, idx) => (
                <Table.HeaderCell
                  key={idx}
                  {...column.getHeaderProps(column.getSortByToggleProps())}
                >
                  {column.render("Header")}
                </Table.HeaderCell>
              ))}
            </Table.Row>
          ))}
        </Table.Header>
        <Table.Body {...getTableBodyProps()}>
          {page.map((row) => {
            prepareRow(row);
            const isExpanded = expanded[row.id];
            const canExpand = row.original.approvals.length > 0;
            return (
              <React.Fragment key={row.id}>
                <Table.Row
                  {...row.getRowProps({
                    onClick: (event) => {
                      toggleAllRowsExpanded(false);
                      if (event.target.tagName !== "BUTTON") {
                        row.toggleRowExpanded(!isExpanded);
                      }
                    },
                  })}
                >
                  {row.cells.map((cell, idx) => {
                    const { key } = cell.getCellProps();
                    return (
                      <Table.Cell
                        className={`${
                          idx === 0 &&
                          `approval-table-cell-first accordion title cap_partially_rejected ${getApprovalTableFlairClassName(
                            row.original.status
                          )}`
                        } ${idx === 8 && "approval-table-cell-last"}`}
                        key={key}
                        {...cell.getCellProps()}
                      >
                        {cell.render("Cell")}
                      </Table.Cell>
                    );
                  })}
                </Table.Row>

                {isExpanded && canExpand ? (
                  <Table.Row className="approval-row-expanded assets-row">
                    <td colSpan={7}>
                      <LoadApprovalAssetsAsync campaignId={row.original.campaignID} />
                    </td>
                  </Table.Row>
                ) : null}
              </React.Fragment>
            );
          })}
        </Table.Body>
      </Table>
      <div className="pagination-row" style={{ marginBottom: "4rem" }}>
        <div>{t("campaigns_page_pagination_campaigns_per_page")}</div>
        <Dropdown
          selection
          compact
          value={pageSize}
          options={[
            { key: "10", text: "10", value: 10 },
            { key: "20", text: "20", value: 20 },
            { key: "50", text: "50", value: 50 },
          ]}
          onChange={(_, { value }) => {
            setPageSize(value);
          }}
        />
        <div className="number-of-pages">
          {t("campaigns_number_of_pages", {
            currentPageValue: pageIndex + 1,
            pageNumbersLength: pageOptions.length,
          })}
        </div>
        <div className="arrows">
          <Icon
            name="angle left"
            className={!canPreviousPage ? "disabled" : null}
            onClick={() => previousPage()}
          />
          <Icon
            name="angle right"
            className={!canNextPage ? "disabled" : null}
            onClick={() => nextPage()}
          />
        </div>
      </div>
    </div>
  );
};

export const ApprovalsTableContributor = ({ data, dispatch, t }) => {
  const chunk = (array, size) => {
    const chunkedArr = [];
    for (let i = 0; i < array.length; i += size) {
      chunkedArr.push(array.slice(i, i + size));
    }
    return chunkedArr;
  };

  const handleDownloadAllAssets = async (assets, campaignId) => {
    try {
      const mapped = assets.map((a) => a.media);
      const downloadUrl = await getZippedAssets(mapped, campaignId);
      downloadFromUrl(downloadUrl);
    } catch (error) {
      toast("error", t("campaign_display_assets_download_error"));
    }
  };

  // Card Grid when <=8 images, carousel when more
  const renderAssets = (assets, campaignId) => {
    return (
      <div>
        <Button
          style={{ marginBottom: "1rem" }}
          primary
          onClick={() => handleDownloadAllAssets(assets, campaignId)}
        >
          Download all assets
        </Button>
        {assets?.length > 8 ? (
          <Carousel showThumbs={false} showStatus={false} showIndicators={false}>
            {chunk(assets, 8).map((chunk, index) => (
              <div key={index} style={{ margin: "3rem" }}>
                <Card.Group itemsPerRow={4}>
                  {chunk.map((asset) => (
                    <CardCustom
                      key={asset.id}
                      asset={asset}
                      dispatch={dispatch}
                      campaignId={campaignId}
                    />
                  ))}
                </Card.Group>
              </div>
            ))}
          </Carousel>
        ) : (
          <Card.Group itemsPerRow={4}>
            {assets?.map((asset) => (
              <CardCustom
                key={asset.id}
                asset={asset}
                dispatch={dispatch}
                campaignId={campaignId}
              />
            ))}
          </Card.Group>
        )}
      </div>
    );
  };

  const defaultSorting = [{ id: "status", desc: true }];
  const columns = useMemo(
    () => [
      {
        Header: t("approvals_columns_header_campaign_name"),
        accessor: "name",
      },
      {
        Header: t("approvals_columns_header_assets"),
        accessor: "amountOfAssets",
      },
      {
        Header: t("approvals_columns_header_start_time"),
        accessor: "starts",
      },
      {
        Header: t("approvals_columns_header_end_time"),
        accessor: "ends",
      },
      {
        Header: t("approvals_columns_header_deadline"),
        accessor: "deadline",
      },
      {
        Header: "",
        id: "icons",
        Cell: function IconRow({ row }) {
          const rotate = row.isExpanded ? 90 : 0;
          return (
            <div style={{ display: "flex", justifyContent: "end" }}>
              <Icon
                className="caret right"
                style={{ transform: `rotate(${rotate}deg)`, transition: `transform .1s ease` }}
              />
            </div>
          );
        },
      },
    ],
    []
  );
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    canPreviousPage,
    canNextPage,
    previousPage,
    nextPage,
    pageOptions,
    setPageSize,
    prepareRow,
    toggleAllRowsExpanded,
    state: { pageIndex, pageSize, expanded },
  } = useTable(
    {
      initialState: {
        pageIndex: 0,
        pageSize: 10,
        sortBy: defaultSorting,
      },
      columns,
      data,
      autoResetExpanded: false,
    },
    useSortBy,
    useExpanded,
    usePagination
  );

  const LoadApprovalAssetsAsync = React.memo(({ campaignId }) => {
    const [, setCampaign] = useState();
    useMemo(() => {
      dispatch.assets.loadCampaignApprovalAssets(campaignId);
      setCampaign(campaignId);
    }, [campaignId]);
    const assets = useSelector((state) => state.assets) || {};
    if (assets) {
      return (
        <>
          <div style={{ marginTop: assets.isLoading && "1rem" }}></div>
          <LoadResolver isLoading={assets.isLoading} hideWhenLoading>
            {renderAssets(assets.approvalItems, campaignId)}
          </LoadResolver>
          <div style={{ marginBottom: assets.isLoading && "4rem" }}></div>
        </>
      );
    }
  });

  return (
    <div>
      <Table className="approval-table" {...getTableProps()}>
        <Table.Header>
          {headerGroups.map((headerGroup, idx) => (
            <Table.Row
              className="approval-table-header-row"
              key={idx}
              {...headerGroup.getHeaderGroupProps()}
            >
              {headerGroup.headers.map((column, idx) => (
                <Table.HeaderCell
                  key={idx}
                  {...column.getHeaderProps(column.getSortByToggleProps())}
                >
                  {column.render("Header")}
                </Table.HeaderCell>
              ))}
            </Table.Row>
          ))}
        </Table.Header>
        <Table.Body {...getTableBodyProps()}>
          {page.map((row) => {
            prepareRow(row);
            const isExpanded = expanded[row.id];
            const canExpand = row.original.approvals.length > 0;
            return (
              <React.Fragment key={row.original.campaignID}>
                <Table.Row
                  {...row.getRowProps({
                    onClick: (event) => {
                      toggleAllRowsExpanded(false);
                      if (event.target.tagName !== "BUTTON") {
                        row.toggleRowExpanded(!isExpanded);
                      }
                    },
                  })}
                >
                  {row.cells.map((cell, idx) => {
                    const { key } = cell.getCellProps();
                    return (
                      <Table.Cell
                        className={`${
                          idx === 0 &&
                          `approval-table-cell-first ${
                            row.original.isReady ? "approved" : "pending"
                          }`
                        } ${idx === 5 && "approval-table-cell-last"}`}
                        key={key}
                        {...cell.getCellProps()}
                      >
                        {cell.render("Cell")}
                      </Table.Cell>
                    );
                  })}
                </Table.Row>

                {isExpanded && canExpand ? (
                  <Table.Row className="approval-row-expanded assets-row">
                    <td colSpan={7}>
                      <LoadApprovalAssetsAsync campaignId={row.original.campaignID} />
                    </td>
                  </Table.Row>
                ) : null}
              </React.Fragment>
            );
          })}
        </Table.Body>
      </Table>
      <div className="pagination-row" style={{ marginBottom: "4rem" }}>
        <div>{t("campaigns_page_pagination_campaigns_per_page")}</div>
        <Dropdown
          selection
          compact
          value={pageSize}
          options={[
            { key: "10", text: "10", value: 10 },
            { key: "20", text: "20", value: 20 },
            { key: "50", text: "50", value: 50 },
          ]}
          onChange={(_, { value }) => {
            setPageSize(value);
          }}
        />
        <div className="number-of-pages">
          {t("campaigns_number_of_pages", {
            currentPageValue: pageIndex + 1,
            pageNumbersLength: pageOptions.length,
          })}
        </div>
        <div className="arrows">
          <Icon
            name="angle left"
            className={!canPreviousPage ? "disabled" : null}
            onClick={() => previousPage()}
          />
          <Icon
            name="angle right"
            className={!canNextPage ? "disabled" : null}
            onClick={() => nextPage()}
          />
        </div>
      </div>
    </div>
  );
};

// ASSET CARDS FOR CONTRIBUTOR
const CardCustom = ({ asset, dispatch, campaignId }) => {
  const [reason, setReason] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [showMediaModal, setShowMediaModal] = useState(false);
  const [showDecisionButtons, setShowDecisionButtons] = useState(false);
  const isApproved = asset.approvals.every((approval) => approval.status === "APPROVED");
  const isRejected = asset.approvals.every((approval) => approval.status === "REJECTED");

  const { t } = useTranslation("common");

  const handleApprove = () => {
    const approvalIds = asset.approvals.map((a) => a.id);
    try {
      dispatch.approvals.updateMultiple({
        approvalIds: approvalIds,
        status: "APPROVED",
        campaignId: campaignId,
      });
      toast(undefined, t("approval_asset_approved"));
    } catch (error) {
      toast("error", `${t("approval_asseet_rejected_error")}: ${error}`);
    }
    setShowDecisionButtons(false);
  };
  const handleReject = () => {
    setModalOpen(true);
  };
  const handleModalSubmit = () => {
    const approvalIds = asset.approvals.map((a) => a.id);
    try {
      dispatch.approvals.updateMultiple({
        approvalIds: approvalIds,
        status: "REJECTED",
        reason: reason,
        campaignId: campaignId,
      });
      toast(undefined, t("approval_asset_rejected"));
    } catch (error) {
      toast("error", error);
    }
    setModalOpen(false);
    setShowDecisionButtons(false);
  };
  const handleModalCancel = () => {
    setModalOpen(false);
  };
  const showEditButton = (isApproved, isRejected) => {
    return isApproved || isRejected;
  };
  return (
    <Card
      key={asset.id}
      className={`pending-card ${isApproved && "approved-card"} ${isRejected && "rejected-card"}`}
    >
      <Card.Content>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            maxHeight: "150px",
            overflow: "hidden",
          }}
        >
          <a href={asset.mediaUrl} download>
            <Icon
              name={"download"}
              color="grey"
              style={{
                zIndex: 10,
                position: "absolute",
                top: 0,
                right: 0,
                padding: "0.8rem 1.5rem 0rem 0rem",
              }}
            />
          </a>
          {!(asset.type.includes("zip") || asset.type.includes("pdf")) && (
            <Icon
              name={asset.type.includes("video") ? "play" : "eye"}
              color="grey"
              style={{
                zIndex: 10,
                position: "absolute",
                top: "30px",
                right: 0,
                padding: "0.8rem 1.5rem 0rem 0rem",
              }}
              onClick={() => setShowMediaModal(true)}
            />
          )}
          <AssetImage asset={asset} />
        </div>
        <Card.Header
          style={{
            marginTop: "1rem",
            wordBreak: "break-all",
            display: "flex",
            fontSize: "14px",
          }}
        >
          <Popup
            content={asset.name}
            position="top center"
            on="hover"
            disabled={!asset.name || formatInformationLength(asset.name) === asset.name}
            trigger={<span>{formatInformationLength(asset.name)}</span>}
          />
        </Card.Header>
        <Card.Meta style={{ display: "flex", justifyContent: "start", padding: 0 }}>
          <div>
            <span>
              {formatInformationLength(asset.size)} |&nbsp;
              {formatInformationLength(asset.type)}
            </span>
          </div>
        </Card.Meta>
        {(!isApproved && !isRejected) || showDecisionButtons ? (
          <div className="ui two buttons">
            <Button basic style={{ padding: "1rem 0rem" }} onClick={() => handleApprove()}>
              <Icon name="checkmark" color="green" /> Approve
            </Button>
            <Button basic style={{ padding: "1rem 0rem" }} onClick={() => handleReject()}>
              <Icon name="remove" color="red" /> Reject
            </Button>
          </div>
        ) : (
          <Card.Description style={{ padding: "0rem", textAlign: "left" }}>
            <div>{asset.reason}</div>
          </Card.Description>
        )}
      </Card.Content>
      {showEditButton(isApproved, isRejected) && (
        <Icon
          name={showDecisionButtons ? "close" : "edit outline"}
          onClick={() => setShowDecisionButtons(!showDecisionButtons)}
          color="grey"
          style={{
            position: "absolute",
            bottom: 0,
            right: 0,
            padding: "0rem 1.5rem 1.8rem 0rem",
          }}
        />
      )}
      <Modal open={modalOpen} onClose={handleModalCancel}>
        <Modal.Header>Reason for Rejection</Modal.Header>
        <Modal.Content>
          <Form name="form-rejection">
            <Form.Field>
              <label htmlFor="form-rejection">Reason</label>
              <input
                placeholder={t("approvals_provide_reason_ph")}
                value={reason}
                onChange={(event) => setReason(event.target.value)}
              />
            </Form.Field>
          </Form>
        </Modal.Content>
        <Modal.Actions>
          <Button color="red" onClick={handleModalCancel}>
            <Icon name="remove" /> Cancel
          </Button>
          <Button color="green" onClick={handleModalSubmit}>
            <Icon name="checkmark" /> Submit
          </Button>
        </Modal.Actions>
      </Modal>
      <MediaPreview
        mediaUrl={asset.mediaUrl}
        isOpen={showMediaModal}
        type={asset.type}
        onClose={() => setShowMediaModal(false)}
        t={t}
      />
    </Card>
  );
};

const CardCustomAdminModalContent = ({ asset, approvals, dispatch }) => {
  useEffect(() => {
    dispatch.assets.loadAssetAdditionalApproverInfo(asset);
  }, []);

  const assets = useSelector((state) => state.assets);
  const { assetApprovers, isAssetApproversLoading } = assets;

  return (
    <Modal.Content style={{ marginTop: isAssetApproversLoading && "2rem" }}>
      <LoadResolver isLoading={isAssetApproversLoading} hideWhenLoading={true}>
        <List>
          <CardCustomAdminManageRow
            approvals={approvals}
            assetApprovers={assetApprovers}
            status={"APPROVED"}
          />
          <Divider style={{ marginTop: "2rem" }} />
          <CardCustomAdminManageRow
            approvals={approvals}
            assetApprovers={assetApprovers}
            status={"REJECTED"}
          />
          <Divider style={{ marginTop: "2rem" }} />
          <CardCustomAdminManageRow
            approvals={approvals}
            assetApprovers={assetApprovers}
            status={"PENDING"}
          />
        </List>
      </LoadResolver>
    </Modal.Content>
  );
};

const CardCustomAdmin = ({ asset, dispatch }) => {
  const [showManageModal, setShowManageModal] = useState(false);
  const [showMediaModal, setShowMediaModal] = useState(false);
  const isApproved = asset.approvals.every((approval) => approval.status === "APPROVED");
  const isPending = asset.approvals.some((approval) => approval.status === "PENDING");
  const isRejected = asset.approvals.every((approval) => approval.status === "REJECTED");
  const isPartiallyRejected = !isPending && !isApproved && !isRejected;
  const approvals = asset.approvals;
  const { t } = useTranslation("common");
  const handleModalCancel = () => {
    setShowManageModal(false);
  };
  return (
    <Card
      key={asset.id}
      className={`
      ${isPending && "pending-card"} 
      ${isApproved && "approved-card"}
      ${isPartiallyRejected && "partially-rejected-card"} 
      ${isRejected && "rejected-card"}`}
    >
      <Card.Content>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            maxHeight: "150px",
            overflow: "hidden",
          }}
        >
          <a href={asset.mediaUrl} download>
            <Icon
              name={"download"}
              color="grey"
              style={{
                zIndex: 10,
                position: "absolute",
                top: 0,
                right: 0,
                padding: "0.8rem 1.5rem 0rem 0rem",
              }}
            />
          </a>
          {!(asset.type.includes("zip") || asset.type.includes("pdf")) && (
            <Icon
              name={asset.type.includes("video") ? "play" : "eye"}
              color="grey"
              style={{
                zIndex: 10,
                position: "absolute",
                top: "30px",
                right: 0,
                padding: "0.8rem 1.5rem 0rem 0rem",
              }}
              onClick={() => setShowMediaModal(true)}
            />
          )}
          <AssetImage asset={asset} />
        </div>
        <Card.Header
          style={{
            marginTop: "1rem",
            wordBreak: "break-all",
            display: "flex",
            fontSize: "14px",
          }}
        >
          <Popup
            content={asset.name}
            position="top center"
            on="hover"
            disabled={!asset.name || formatInformationLength(asset.name) === asset.name}
            trigger={<span>{formatInformationLength(asset.name)}</span>}
          />
        </Card.Header>
        <Card.Meta style={{ display: "flex", justifyContent: "start", padding: 0 }}>
          <div>
            <span>
              {formatInformationLength(asset.size)} |&nbsp;
              {formatInformationLength(asset.type)}
            </span>
          </div>
        </Card.Meta>
      </Card.Content>
      <Icon
        name={"edit outline"}
        onClick={() => setShowManageModal(true)}
        color="grey"
        style={{
          position: "absolute",
          bottom: 0,
          right: 0,
          padding: "0rem 1.5rem 1.8rem 0rem",
        }}
      />
      <Modal open={showManageModal} onClose={handleModalCancel}>
        <Modal.Header>
          {t("approvals_approvals_for_asset")}: {asset.name}
        </Modal.Header>

        <CardCustomAdminModalContent
          asset={asset}
          approvals={approvals}
          dispatch={dispatch}
        ></CardCustomAdminModalContent>
        <Modal.Actions>
          <Button
            style={{
              color: "white",
              backgroundColor: "#4700a3",
            }}
            onClick={handleModalCancel}
          >
            {t("close")}
          </Button>
        </Modal.Actions>
      </Modal>
      <MediaPreview
        mediaUrl={asset.mediaUrl}
        isOpen={showMediaModal}
        type={asset.type}
        onClose={() => setShowMediaModal(false)}
        t={t}
      />
    </Card>
  );
};

const CardCustomAdminManageRow = ({ approvals, assetApprovers, handleApprovalChange, status }) => {
  const { t } = useTranslation("common");
  return (
    <>
      <Header>
        {status === "APPROVED" ? (
          <>
            <Icon name="checkmark" color="green" />
            {t("approvals_approved")}
          </>
        ) : status === "REJECTED" ? (
          <>
            <Icon name="remove" color="red" />
            {t("approvals_rejected")}
          </>
        ) : (
          <>
            <Icon name="question circle outline" />
            {t("approvals_pending")}
          </>
        )}
      </Header>
      {approvals
        .filter((approval) => approval.status === status)
        .map(({ id, approverID, reason }) => (
          <List.Item key={id}>
            <List.Content style={{ display: "flex" }}>
              <span style={{ display: "flex", alignItems: "center", marginLeft: "10px" }}>
                {assetApprovers[approverID]?.name}
                {reason && reason !== "" && (
                  <span style={{ fontWeight: 600 }}>&nbsp;&quot;{reason}&quot;</span>
                )}
              </span>
            </List.Content>
          </List.Item>
        ))}
    </>
  );
};
